import React, { useCallback, useState } from 'react';

interface FileUploadProps {
  onFileUpload: (file: File, content: string) => void;
  onStartNew?: () => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileUpload, onStartNew }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFile = useCallback((file: File) => {
    console.log('File selected:', file.name, 'Type:', file.type);
    
    // Accept .txt and .json files regardless of MIME type
    const isTxt = file.name.endsWith('.txt') || file.type === 'text/plain' || file.type === '';
    const isJson = file.name.endsWith('.json') || file.type === 'application/json';
    if (!isTxt && !isJson) {
      alert('Please upload a .txt or .json file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      console.log('File read successfully, length:', content.length);
      onFileUpload(file, content);
    };
    reader.onerror = (e) => {
      console.error('Error reading file:', e);
      alert('Error reading file. Please try again.');
    };
    reader.readAsText(file);
  }, [onFileUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  return (
    <div className="max-w-2xl mx-auto">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="space-y-4">
          <div className="mx-auto w-12 h-12 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Upload NER Comparison Data
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Drag and drop a .json file with text of annotating or model outputs, or click to browse
            </p>
          </div>

          <div className="flex items-center justify-center gap-3">
            <label className="cursor-pointer">
              <span className="mt-2 block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                Choose File
              </span>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleFileInput}
                className="hidden"
              />
            </label>
             or 
            {onStartNew && (
              <button
                type="button"
                onClick={onStartNew}
                className="mt-2 px-4 py-2 bg-gray-100 text-gray-900 rounded-md hover:bg-gray-200 border border-gray-300 transition-colors"
              >
                Start new project
              </button>
            )}
          </div>

          <div className="text-xs text-gray-500 space-y-3">
            <div>
              <p className="font-medium">JSON Format (recommended):</p>
              <p className="text-[11px] text-gray-500 mt-1">
                See <a href="/data-schema.json" target="_blank" className="text-blue-600 hover:underline">data-schema.json</a> for complete schema
              </p>
              <div className="mt-2 text-left bg-gray-100 p-3 rounded text-[11px] font-mono whitespace-pre-wrap break-words">
{`{
  "questions": [
    {
      "id": "q1",
      "text": "OpenAI was founded in San Francisco in 2015.",
      "humanAnnotations": [
        { "text": "OpenAI", "label": "Organization", "start": 0, "end": 6 }
      ],
      "modelResponses": [
        {
          "modelName": "Model A",
          "inferenceTime": 0.045,
          "entities": [
            { "text": "OpenAI", "label": "ORG", "start": 0, "end": 6, "score": 0.95 }
          ]
        }
      ]
    }
  ],
  "modelNames": ["Model A"]
}`}
              </div>
            </div>
            <p className="text-gray-600">
              Supports: inferenceTime, positions, confidence scores, custom colors, saved themes
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
